import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import './App.css'; 
// Import your components and pages
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { PilotIntakeProvider } from './components/PilotIntake';
import Home from './pages/Home';
import About from './pages/About';
import ProductDetail from './pages/ProductDetail';
import WhyChooseUs from './pages/WhyChooseUs';
import Contact from './pages/Contact';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import StudentDashboard from './pages/Student/StudentDashboard';
import ProfessorDashboard from './pages/Professor/ProfessorDashboard';
import AdminDashboard from './pages/Admin/AdminDashboard';
import NotFound from './pages/NotFound';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Pricing from './pages/Pricing';
import ThankYou from './pages/ThankYou';
import ProtectedRoute from './components/ProtectedRoute';
import { AuthProvider } from './contexts/AuthContext';

const isNativeApp = () => Capacitor.isNativePlatform();

function RootRoute({ theme }) {
  return isNativeApp() ? <Navigate to="/sign-in" replace /> : <Home theme={theme} />;
}

function AppWrapper() {
  const location = useLocation();
  const nativeApp = isNativeApp();

  // --------------------------
  // THEME LOGIC: Add this code
  // --------------------------

  // 1. Initialize theme from localStorage or default to 'light'
  const [theme, setTheme] = useState(() => {
    const storedTheme = localStorage.getItem('theme');
    return storedTheme ? storedTheme : 'light';
  });

  // 2. Apply the theme class to <html> and store preference
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // 3. Toggle function
  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  // --------------------------------
  // BANNER / NAVBAR / FOOTER LOGIC
  // --------------------------------
  const [showBanner, setShowBanner] = useState(false);

  const hideBannerRoutes = [
    '/student',
    '/professor',
    '/admin',
  ];

  const shouldHideBanner = hideBannerRoutes.includes(location.pathname);
  const shouldHideChrome = hideBannerRoutes.includes(location.pathname) ||
    (nativeApp && ['/sign-in', '/sign-up'].includes(location.pathname));

  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  return (
    <>
      {/* Tailwind‑styled banner */}
      {!shouldHideBanner && showBanner && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-400 text-black text-center py-2 px-4 z-[1050] flex items-center justify-center">
          <span>
            <strong>Testing Phase:</strong> This is a testing phase. Please contact{' '}
            <a href="mailto:info@coursengage.com" className="underline text-blue-600">
              info@coursengage.com
            </a>{' '}
            to participate, and we will send you the innovation code for sign up. All functionalities are currently not working.
          </span>
          <button
            type="button"
            onClick={() => setShowBanner(false)}
            className="ml-4 text-xl font-bold focus:outline-none"
            aria-label="Close banner"
          >
            &times;
          </button>
        </div>
      )}

      {/* Add top padding when banner is visible so content isn't hidden */}
      <div className={!shouldHideBanner && showBanner ? 'pt-12' : ''}>
        {/* Pass theme info and toggleTheme to Navbar */}
        {!shouldHideChrome && (
          <Navbar theme={theme} toggleTheme={toggleTheme} />
        )}

        {/* Define Routes */}
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<RootRoute theme={theme} />} />
          <Route path="/about" element={<About />} />
          <Route path="/privacypolicy" element={<PrivacyPolicy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/product-detail" element={<ProductDetail />} />
          <Route path="/why-choose-us" element={<WhyChooseUs />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/sign-in" element={<SignIn />} />
          <Route path="/sign-up" element={<SignUp />} />

          {/* Protected Routes */}

          {/* Student Routes */}
          <Route
            path="/student"
            element={
              <ProtectedRoute requiredRole="student">
                <StudentDashboard />
              </ProtectedRoute>
            }
          />

          {/* Professor Routes */}
          <Route
            path="/professor"
            element={
              <ProtectedRoute requiredRole="professor">
                <ProfessorDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Conditionally render Footer */}
        {!shouldHideChrome && <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <PilotIntakeProvider>
          <AppWrapper />
        </PilotIntakeProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;
